"use client"

import { motion } from "motion/react"
import Link from "next/link"
import { memo, useCallback, useEffect, useRef, useState } from "react"
import {
    siAirbnb,
    siAlipay,
    siApple,
    siAtlassian,
    siDiscord,
    siDropbox,
    siFacebook,
    siFigma,
    siGithub,
    siGoogle,
    siIntel,
    siJira,
    siMeta,
    siMongodb,
    siNetflix,
    siNodedotjs,
    siNotion,
    siNvidia,
    siPaypal,
    siSamsung,
    siShopify,
    siSpotify,
    siStripe,
    siTesla,
    siUber,
    siVercel,
    siYoutube,
    siZoom
} from "simple-icons"

import { cn } from "@/core/utils/utils"

// Helper to render simple-icons SVG
interface SimpleIconProps {
  icon: { path: string; hex: string }
  className?: string
  style?: React.CSSProperties
}

const SimpleIcon = memo(({ icon, className, style }: SimpleIconProps) => (
  <svg
    role="img"
    viewBox="0 0 24 24"
    className={className}
    style={style}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d={icon.path} />
  </svg>
))

SimpleIcon.displayName = "SimpleIcon"

// Partner data - replace with your actual clients/partners
const partners = [
  { name: "Google", icon: siGoogle, color: `#${siGoogle.hex}`, url: "https://www.google.com" },
  { name: "Apple", icon: siApple, color: `#${siApple.hex}`, url: "https://www.apple.com" },
  { name: "Meta", icon: siMeta, color: `#${siMeta.hex}`, url: "https://www.meta.com" },
  { name: "Netflix", icon: siNetflix, color: `#${siNetflix.hex}`, url: "https://www.netflix.com" },
  { name: "Spotify", icon: siSpotify, color: `#${siSpotify.hex}`, url: "https://www.spotify.com" },
  { name: "Tesla", icon: siTesla, color: `#${siTesla.hex}`, url: "https://www.tesla.com" },
  { name: "Uber", icon: siUber, color: `#${siUber.hex}`, url: "https://www.uber.com" },
  { name: "Airbnb", icon: siAirbnb, color: `#${siAirbnb.hex}`, url: "https://www.airbnb.com" },
  { name: "Stripe", icon: siStripe, color: `#${siStripe.hex}`, url: "https://stripe.com" },
  { name: "Shopify", icon: siShopify, color: `#${siShopify.hex}`, url: "https://www.shopify.com" },
  { name: "Github", icon: siGithub, color: `#${siGithub.hex}`, url: "https://github.com" },
  { name: "Paypal", icon: siPaypal, color: `#${siPaypal.hex}`, url: "https://www.paypal.com" },
  { name: "Intel", icon: siIntel, color: `#${siIntel.hex}`, url: "https://www.intel.com" },
  { name: "Nvidia", icon: siNvidia, color: `#${siNvidia.hex}`, url: "https://www.nvidia.com" },
  { name: "Facebook", icon: siFacebook, color: `#${siFacebook.hex}`, url: "https://www.facebook.com" },
  { name: "Youtube", icon: siYoutube, color: `#${siYoutube.hex}`, url: "https://www.youtube.com" },
  { name: "Discord", icon: siDiscord, color: `#${siDiscord.hex}`, url: "https://discord.com" },
  { name: "Zoom", icon: siZoom, color: `#${siZoom.hex}`, url: "https://zoom.us" },
  { name: "Dropbox", icon: siDropbox, color: `#${siDropbox.hex}`, url: "https://www.dropbox.com" },
  { name: "Atlassian", icon: siAtlassian, color: `#${siAtlassian.hex}`, url: "https://www.atlassian.com" },
  { name: "Jira", icon: siJira, color: `#${siJira.hex}`, url: "https://www.atlassian.com/software/jira" },
  { name: "Notion", icon: siNotion, color: `#${siNotion.hex}`, url: "https://www.notion.so" },
  { name: "Figma", icon: siFigma, color: `#${siFigma.hex}`, url: "https://www.figma.com" },
  { name: "Vercel", icon: siVercel, color: `#${siVercel.hex}`, url: "https://vercel.com" },
  { name: "MongoDB", icon: siMongodb, color: `#${siMongodb.hex}`, url: "https://www.mongodb.com" },
  { name: "Node.js", icon: siNodedotjs, color: `#${siNodedotjs.hex}`, url: "https://nodejs.org" },
  { name: "Samsung", icon: siSamsung, color: `#${siSamsung.hex}`, url: "https://www.samsung.com" },
  { name: "Alipay", icon: siAlipay, color: `#${siAlipay.hex}`, url: "https://www.alipay.com" },
] as const

// Memoize duplicated partners array
const duplicatedPartners = [...partners, ...partners]

export default function TrustedBy() {
  const [isPaused, setIsPaused] = useState(false)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [isInView, setIsInView] = useState(true)
  const sectionRef = useRef<HTMLElement>(null)

  // Performance: Pause animation when not in viewport
  useEffect(() => {
    if (typeof window === "undefined" || !sectionRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting)
        })
      },
      { threshold: 0.1 }
    )

    observer.observe(sectionRef.current)

    return () => {
      observer.disconnect()
    }
  }, [])

  // Memoize hover handlers to prevent unnecessary re-renders
  const handleMouseEnter = useCallback(() => {
    setIsPaused(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsPaused(false)
  }, [])

  const handleCardHover = useCallback((index: number) => {
    setHoveredIndex(index)
  }, [])

  const handleCardLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  // Combine pause states: pause if section hovered OR not in view
  const shouldPause = isPaused || !isInView

  return (
    <section 
      ref={sectionRef}
      className="relative py-6 md:py-8 overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="Trusted by companies"
    >
      {/* Subtle background gradient */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background: `radial-gradient(ellipse 80% 50% at 50% 50%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)`,
        }}
        aria-hidden="true"
      />

      {/* Noise texture */}
      <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-[0.015] mix-blend-overlay pointer-events-none" aria-hidden="true" />

      {/* Marquee Container */}
      <div className="relative overflow-hidden">
        {/* Left Fade */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-linear-to-r from-black via-black/60 to-transparent z-20 pointer-events-none" aria-hidden="true" />
        
        {/* Right Fade */}
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-linear-to-l from-black via-black/60 to-transparent z-20 pointer-events-none" aria-hidden="true" />

        {/* Marquee Strip */}
        <div
          className={cn(
            "flex gap-4 items-center h-24",
            "marquee-horizontal"
          )}
          style={{
            width: "max-content",
            animationPlayState: shouldPause ? "paused" : "running",
          }}
          aria-live="polite"
        >
          {duplicatedPartners.map((partner, index) => (
            <PartnerCard
              key={`${partner.name}-${index}`}
              partner={partner}
              index={index}
              isHovered={hoveredIndex === index}
              onHover={handleCardHover}
              onLeave={handleCardLeave}
            />
          ))}
        </div>
      </div>

    </section>
  )
}

// Memoize PartnerCard to prevent unnecessary re-renders
const PartnerCard = memo(function PartnerCard({
  partner,
  index,
  isHovered,
  onHover,
  onLeave,
}: {
  partner: typeof partners[number]
  index: number
  isHovered: boolean
  onHover: (index: number) => void
  onLeave: () => void
}) {
  const handleMouseEnter = useCallback(() => {
    onHover(index)
  }, [index, onHover])

  return (
    <div
      className="shrink-0"
    >
      <Link
        href={partner.url}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onLeave}
        className={cn(
          "relative flex items-center gap-3",
          "px-5 py-3.5 rounded-full",
          "bg-white/2 backdrop-blur-xs",
          "border border-white/5",
          "transition-all duration-200",
          "cursor-pointer",
          "min-w-fit",
          "no-underline",
          "inline-flex",
          isHovered && "bg-white/4 border-white/15 scale-105 px-7"
        )}
        aria-label={`Visit ${partner.name}`}
      >
      {/* Icon */}
      <SimpleIcon
        icon={partner.icon}
        className="w-7 h-7 transition-all duration-200"
        style={{
          color: isHovered ? partner.color : "hsl(var(--foreground))",
          filter: isHovered ? `drop-shadow(0 0 8px ${partner.color}60)` : "none",
          transform: isHovered ? "scale(1.1)" : "scale(1)",
        }}
      />

      {/* Company Name - Simple reveal on hover */}
      <motion.div
        initial={false}
        animate={{
          width: isHovered ? "auto" : 0,
          opacity: isHovered ? 1 : 0,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="overflow-hidden"
      >
        <span
          className="text-xs font-medium text-foreground/60 whitespace-nowrap"
          style={{
            textShadow: isHovered ? `0 0 6px ${partner.color}30` : "none",
          }}
        >
          {partner.name}
        </span>
      </motion.div>

      {/* Glow effect on hover */}
      {isHovered && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none transition-opacity duration-200"
          style={{
            background: `radial-gradient(circle at center, ${partner.color}10, transparent 70%)`,
            boxShadow: `0 0 20px ${partner.color}15`,
          }}
          aria-hidden="true"
        />
      )}
      </Link>
    </div>
  )
})
